import type { ListingRun } from "@nova-ai/shared"
import { Link } from "@tanstack/react-router"
import { Check, ChevronRight, Clock, TriangleAlert } from "lucide-react"
import { formatDateTime } from "../../lib/format-date"
import { humanizeMarketplaceId } from "../../lib/marketplace"
import {
  deriveRunReadiness,
  formatRunNumber,
  runFeedTotals,
  runSourceLabel,
} from "../../lib/runs-list"
import { cn } from "../../lib/utils"
import { MarketplaceMark } from "../marketplaces/marketplace-mark"
import { StatusBadge } from "../ui/status-badge"
import { splitActor } from "./actor-label"
import { RunStatusBadge } from "./run-status-badge"

function RunReadiness({ run }: { run: ListingRun }) {
  const ready = deriveRunReadiness(run)
  const Icon = { ready: Check, fix: TriangleAlert, unchecked: Clock }[ready.key]
  return (
    <div
      data-run-readiness
      className="grid gap-0.5 self-stretch rounded-l-[10px] bg-[color-mix(in_oklch,var(--color-muted)_45%,var(--color-card))] px-3 py-2 sm:self-auto sm:rounded-none sm:bg-transparent sm:p-0"
    >
      <span
        className={cn(
          "inline-flex items-center gap-1.5 text-[13px] font-semibold sm:whitespace-nowrap",
          {
            "text-success": ready.tone === "success",
            "text-warning": ready.tone === "warning",
            "text-muted-foreground": ready.tone === "muted",
          }
        )}
      >
        <Icon className="size-3.5 flex-none" />

        {ready.label}
      </span>

      <span className="text-[12px] text-muted-foreground sm:whitespace-nowrap">{ready.note}</span>
    </div>
  )
}
function RunIdentity({ run, showStatus }: { run: ListingRun; showStatus: boolean }) {
  const actor = splitActor(run.startedBy)
  const handle = actor.provider === "system" ? "Scheduled" : actor.handle.split("@")[0]
  const scope = runSourceLabel(run)
  const category = run.masterCategory
    ? run.masterCategory.charAt(0).toUpperCase() + run.masterCategory.slice(1)
    : "Unassigned"
  return (
    <div className="col-span-2 flex min-w-0 items-center gap-3 pr-9 sm:col-span-1 sm:pr-0">
      <MarketplaceMark marketplace={run.marketplace} className="size-8" showCountryCode />

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Link
            className="font-semibold wrap-anywhere text-foreground no-underline after:absolute after:inset-0 after:z-1 after:content-[''] group-hover/run:underline group-hover/run:underline-offset-3 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
            to="/runs/$slug"
            params={{ slug: run.slug }}
          >
            {humanizeMarketplaceId(run.marketplace)}
          </Link>

          {showStatus && <RunStatusBadge status={run.status} />}

          {run.archivedAt && (
            <StatusBadge tone="muted" size="sm">
              Archived
            </StatusBadge>
          )}

          {run.origin === "automatic" && (
            <>
              <StatusBadge tone="brand" size="sm">
                Automatic relist
              </StatusBadge>

              <StatusBadge tone={run.automaticDirtyAt ? "warning" : "success"} size="sm">
                {run.automaticDirtyAt ? "Dirty" : "Current"}
              </StatusBadge>
            </>
          )}
        </div>

        <div
          className="mt-0.5 truncate text-[12px] text-muted-foreground"
          title={`${category} · ${scope} · ${run.startedBy}`}
        >
          {`${category} · ${scope} · ${handle}`}
        </div>

        <div className="mt-0.5 overflow-hidden text-[12px] text-ellipsis whitespace-nowrap text-muted-foreground tabular-nums sm:overflow-visible sm:text-clip">
          <time dateTime={run.createdAt}>{`${formatDateTime(run.createdAt)} · `}</time>

          <span className="font-mono text-[11px]">{run.slug}</span>
        </div>
      </div>
    </div>
  )
}
export function RunsTable({
  runs,
  showStatus = false,
}: {
  runs: readonly ListingRun[]
  showStatus?: boolean
}) {
  const totals = runFeedTotals(runs)
  return (
    <>
      <div
        className="hidden grid-cols-[minmax(0,1fr)_150px_96px_32px] items-center gap-4 border-b border-border px-4 py-2.5 text-[12px] text-muted-foreground sm:grid lg:grid-cols-[minmax(0,1fr)_168px_112px_104px_32px]"
        aria-hidden="true"
      >
        <span>Run</span>

        <span>Readiness</span>

        <span>Offers needed</span>

        <span className="hidden lg:block">Feed rows</span>

        <span />
      </div>

      <ol className="m-0 list-none p-0">
        {runs.map((run) => (
          <li
            data-runs-row
            key={run.id}
            className="group/run relative grid cursor-pointer grid-cols-2 items-center gap-x-0 gap-y-3 p-4 transition-[background] duration-150 ease-[ease] not-first:border-t not-first:border-border/70 hover:bg-[color-mix(in_oklch,var(--color-muted)_30%,var(--color-card))] has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-ring has-[:focus-visible]:-outline-offset-2 motion-reduce:transition-none sm:grid-cols-[minmax(0,1fr)_150px_96px_32px] sm:gap-4 sm:py-3.5 lg:grid-cols-[minmax(0,1fr)_168px_112px_104px_32px]"
          >
            <RunIdentity run={run} showStatus={showStatus} />

            <RunReadiness run={run} />

            <div className="self-stretch rounded-r-[10px] border-l border-border bg-[color-mix(in_oklch,var(--color-muted)_45%,var(--color-card))] px-3 py-2 text-[13px] whitespace-nowrap tabular-nums sm:self-auto sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0">
              <OffersNeeded count={run.offersNeededCount} />

              <span className="block text-[12px] text-muted-foreground">offers needed</span>
            </div>

            <div className="hidden text-[13px] whitespace-nowrap tabular-nums lg:block">
              {run.feedRowCount === null ? (
                <span className="text-muted-foreground">Not built</span>
              ) : (
                <b>{formatRunNumber(run.feedRowCount)}</b>
              )}

              <span className="block text-[12px] text-muted-foreground">feed rows</span>
            </div>

            <span
              className="absolute top-4 right-3 flex size-8 items-center justify-center rounded-full text-muted-foreground transition-[background,transform] duration-150 ease-[ease] group-hover/run:translate-x-0.5 group-hover/run:bg-muted group-hover/run:text-foreground motion-reduce:transition-none sm:static"
              aria-hidden="true"
            >
              <ChevronRight className="size-4 flex-none" />
            </span>
          </li>
        ))}
      </ol>

      <footer className="flex flex-wrap justify-between gap-2 border-t border-border px-4 py-3 text-[12px] text-muted-foreground tabular-nums">
        <span>Newest first</span>

        <span>
          {`${formatRunNumber(totals.rows)} feed rows built by ${totals.built} of ${totals.total} runs`}
        </span>
      </footer>
    </>
  )
}

function OffersNeeded({ count }: { count: number | null }) {
  if (count === null) {
    return <span className="text-muted-foreground">—</span>
  }
  if (count === 0) {
    return <span className="text-muted-foreground">None</span>
  }
  return <b className="text-warning">{formatRunNumber(count)}</b>
}
